import { getTelemetryClient } from 'smash-telemetry';
import { environment } from '../../../environments/environment';
import { scrubHttpErrorForTelemetry } from './scrubHttpErrorForTelemetry';

declare global {
    interface Window {
        /**
         * Set by the pre-Angular outdated-browser detector in `src/index.html` (design
         * `docs/design/2026-10-28-browser-support-compliance-and-outdated-browser-notice.md`,
         * section 2.7.1) when the visitor's engine is missing one of the ES2022 runtime methods the
         * browser-support policy floors on. Read here so a below-floor browser that still boots far
         * enough to run Angular is filterable and countable in telemetry, rather than indistinguishable
         * from an in-policy one.
         */
        __smashBelowBrowserFloor?: boolean;
    }
}

/**
 * Initializes and starts the shared `smash-telemetry` client, browser-only, from this
 * environment's configuration. No consent gate: telemetry here is error/console/Web Vitals
 * observability, not marketing analytics, so it runs unconditionally.
 *
 * `beforeSend` is wired to {@link scrubHttpErrorForTelemetry} so an `HttpErrorResponse` captured
 * through `GlobalErrorHandler` never leaves the browser with its request URL, query string or
 * response body intact -- `smash-telemetry`'s ingestion endpoint is unauthenticated.
 *
 * `context.belowBrowserFloor` carries the outdated-browser flag (see {@link Window.__smashBelowBrowserFloor}),
 * always present as an explicit boolean rather than only when `true`. `smash-telemetry`'s `context` option
 * is attached once per BATCH envelope, not per event (a batch can carry up to `maximumBatchEvents`, default
 * 100, events) -- so a Logs Insights query filtering on it per-event needs the ingestion/storage side to
 * fan the batch-level `context` down onto each stored log record; that is unverified from this repo.
 */
export function bootstrapTelemetry(): void {
    const client = getTelemetryClient();
    client.init({
        logGroup: environment.telemetry.logGroup,
        stream: environment.telemetry.stream,
        baseUrl: environment.telemetry.baseUrl,
        enabled: environment.telemetry.enabled,
        beforeSend: scrubHttpErrorForTelemetry,
        context: {
            belowBrowserFloor: typeof window !== 'undefined' && window.__smashBelowBrowserFloor === true,
        },
    });
    client.start();
}
